'use client';

import { Button, Group, rem, Stack, Text } from '@mantine/core';
import { useTranslations } from 'next-intl';
import { Controller, useFormContext } from 'react-hook-form';
import type { LocationFeature } from '../../api/service/types';

interface LocationFeaturesSelectorProps {
  features: LocationFeature[];
}

const LocationFeaturesSelector = ({
  features,
}: LocationFeaturesSelectorProps) => {
  const tBooking = useTranslations('booking');
  const { control } = useFormContext();

  if (!features || features.length === 0) return null;

  return (
    <Stack gap={rem(8)}>
      <Text size="sm" fw={600} c="neutral.7">
        {tBooking('location.features')}
      </Text>
      <Controller
        name="location_features"
        control={control}
        defaultValue={[]}
        render={({ field: { value = [], onChange } }) => {
          const handleToggle = (code: string) => {
            const currentValues = Array.isArray(value) ? value : [];

            // Prevent unselecting if only one option is selected
            if (currentValues.includes(code) && currentValues.length === 1) {
              return;
            }

            const newValues = currentValues.includes(code)
              ? currentValues.filter((item) => item !== code)
              : [...currentValues, code];
            onChange(newValues);
          };

          return (
            <Group gap={rem(8)}>
              {features.map((feature) => {
                const isSelected = Array.isArray(value)
                  ? value.includes(feature.code)
                  : false;

                return (
                  <Button
                    radius={18}
                    key={feature.code}
                    size="compact-md"
                    variant="outline"
                    color={isSelected ? 'primary' : 'neutral'}
                    onClick={() => handleToggle(feature.code)}
                    styles={{
                      root: {
                        borderColor: isSelected
                          ? 'var(--mantine-color-primary-6)'
                          : 'var(--mantine-color-neutral-3)',
                      },
                    }}
                  >
                    {feature.name}
                  </Button>
                );
              })}
            </Group>
          );
        }}
      />
    </Stack>
  );
};

export default LocationFeaturesSelector;
